import { useState } from "react";
import { Loader2, User, Truck, Warehouse, Building2, Store, Users, Plug, Download, Info, ArrowDownToLine, DatabaseBackup } from "lucide-react";
import { toast } from "sonner";
import { authClient } from "@/lib/auth-client";
import { useSession } from "@/hooks/use-auth";
import { hasRole } from "@/lib/roles";
import { useActiveStoreType } from "@/hooks/use-active-store-type";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CredentialsSection } from "@/components/shipping/credentials-section";
import { WarehouseSection } from "@/components/warehouse/warehouse-section";
import { BusinessSettingsSection } from "@/components/fulfillment/business-settings-section";
import { TestEmailSection } from "@/components/fulfillment/test-email-section";
import { StoresSection } from "@/components/stores/stores-section";
import { UsersSection } from "@/components/users/users-section";
import { MigrationSection } from "@/components/migration/migration-section";
import { BackupSection } from "@/components/backup/backup-section";

function ProfileSection() {
  const { user } = useSession();
  const [name, setName] = useState(user?.name ?? "");
  const [profileLoading, setProfileLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    setProfileLoading(true);
    try {
      await authClient.updateUser({ name });
      toast.success("Profil sačuvan");
    } catch (e: any) {
      toast.error(e.message || "Greška pri čuvanju profila");
    } finally {
      setProfileLoading(false);
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPasswordLoading(true);
    try {
      await authClient.changePassword({ currentPassword, newPassword });
      toast.success("Lozinka promijenjena");
      setCurrentPassword("");
      setNewPassword("");
    } catch (e: any) {
      toast.error(e.message || "Greška pri promjeni lozinke");
    } finally {
      setPasswordLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="ring-1 ring-foreground/10">
        <div className="border-b bg-muted/50 px-4 py-2">
          <span className="text-xs font-medium">Profil</span>
        </div>
        <div className="p-4">
          <form onSubmit={handleSaveProfile} className="flex flex-col gap-3 max-w-sm">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="profile-name" className="text-xs">Ime</Label>
              <Input
                id="profile-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="profile-email" className="text-xs">Email</Label>
              <Input
                id="profile-email"
                type="email"
                value={user?.email ?? ""}
                disabled
                className="bg-muted"
              />
            </div>
            <Button type="submit" className="self-start" disabled={profileLoading}>
              {profileLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sačuvaj
            </Button>
          </form>
        </div>
      </div>

      <div className="ring-1 ring-foreground/10">
        <div className="border-b bg-muted/50 px-4 py-2">
          <span className="text-xs font-medium">Promjena lozinke</span>
        </div>
        <div className="p-4">
          <form onSubmit={handleChangePassword} className="flex flex-col gap-3 max-w-sm">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="current-password" className="text-xs">Trenutna lozinka</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-password" className="text-xs">Nova lozinka</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="self-start" disabled={passwordLoading}>
              {passwordLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Promijeni lozinku
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

export function SettingsPage() {
  const { user } = useSession();
  const isAdmin = hasRole(user, "admin");
  const storeType = useActiveStoreType();
  const isWholesale = storeType === "wholesale";

  return (
    <div className="p-4 flex flex-col gap-3">
      <h2 className="text-sm font-medium">Postavke</h2>

      <Tabs defaultValue="profile">
        <TabsList variant="line">
          <TabsTrigger value="profile">
            <User className="h-3.5 w-3.5" />
            Profil
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="shipping">
              <Truck className="h-3.5 w-3.5" />
              Kuriri
            </TabsTrigger>
          )}
          {isAdmin && !isWholesale && (
            <TabsTrigger value="warehouses">
              <Warehouse className="h-3.5 w-3.5" />
              Skladišta
            </TabsTrigger>
          )}
          {isAdmin && !isWholesale && (
            <TabsTrigger value="stores">
              <Store className="h-3.5 w-3.5" />
              Trgovine
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="users">
              <Users className="h-3.5 w-3.5" />
              Korisnici
            </TabsTrigger>
          )}
          <TabsTrigger value="business">
            <Building2 className="h-3.5 w-3.5" />
            Poslovno
          </TabsTrigger>
          {isAdmin && !isWholesale && (
            <TabsTrigger value="plugin">
              <Plug className="h-3.5 w-3.5" />
              Plugin
            </TabsTrigger>
          )}
          {isAdmin && !isWholesale && (
            <TabsTrigger value="migration">
              <ArrowDownToLine className="h-3.5 w-3.5" />
              Migracija
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="backup">
              <DatabaseBackup className="h-3.5 w-3.5" />
              Backup
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile">
          <ProfileSection />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="shipping">
            <CredentialsSection />
          </TabsContent>
        )}

        {isAdmin && !isWholesale && (
          <TabsContent value="warehouses">
            <WarehouseSection />
          </TabsContent>
        )}

        {isAdmin && !isWholesale && (
          <TabsContent value="stores">
            <StoresSection />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="users">
            <UsersSection />
          </TabsContent>
        )}

        <TabsContent value="business">
          <div className="flex flex-col gap-4">
            <BusinessSettingsSection />
            <TestEmailSection />
          </div>
        </TabsContent>

        {isAdmin && !isWholesale && (
          <TabsContent value="plugin">
            <div className="ring-1 ring-foreground/10">
              <div className="border-b bg-muted/50 px-4 py-2">
                <span className="text-xs font-medium">WordPress Plugin</span>
              </div>
              <div className="p-4 flex flex-col gap-3 max-w-sm">
                <p className="text-xs text-muted-foreground">
                  Komoran API plugin za WooCommerce. Potrebno za povezivanje sa Komoran sistemom.
                </p>
                <div className="flex items-start gap-2 bg-blue-500/10 text-blue-700 dark:text-blue-400 px-3 py-2 text-xs">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>Zahtijeva instaliran WooCommerce plugin na WordPress stranici.</span>
                </div>
                <div className="text-xs text-muted-foreground flex flex-col gap-1">
                  <span>Verzija: 1.0.2</span>
                  <span>Vendor: Lunatik d.o.o.</span>
                </div>
                <Button asChild className="self-start gap-1.5">
                  <a href="/api/assets/komoran-api.zip" download>
                    <Download className="h-3.5 w-3.5" />
                    Preuzmi plugin
                  </a>
                </Button>
              </div>
            </div>
          </TabsContent>
        )}

        {isAdmin && !isWholesale && (
          <TabsContent value="migration">
            <MigrationSection />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="backup">
            <BackupSection />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
